import { Check, ExternalLink, Flag, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  useBillingValidationDetail,
  useReviewBillingValidation,
  type BillingValidationCheck,
  type BillingValidationItem,
  type ReviewStatus,
} from "../api";
import { qboCustomerUrl } from "../../contracts/links";
import { fmtCur2, fmtDate } from "../../renewals/format";

const str = (v: unknown): string =>
  v == null || v === "" ? "—" : String(v);

const yesNo = (v: unknown): string => (v == null ? "—" : v ? "Yes" : "No");

const num = (v: unknown): number =>
  v == null ? 0 : typeof v === "number" ? v : Number(v) || 0;

type Rec = Record<string, unknown>;
const arr = (v: unknown): Rec[] => (Array.isArray(v) ? (v as Rec[]) : []);

/** Review drawer for one structural exception.
 *
 *  What's useful differs completely by record type, so the body is routed by
 *  entity rather than shoehorned into one layout: an account's evidence is the
 *  companies hanging off it and the contracts that aren't; a contract's is its
 *  links and money; a company's is which account it claims. All of it comes
 *  from the same Cinch records someone will open to fix the thing. */
export function BillingValidationDrawer({
  period,
  item,
  check,
  canAct,
  expanded,
  presetFlag,
  onExpandedChange,
  onClose,
}: {
  period: string;
  item: BillingValidationItem | null;
  check: BillingValidationCheck | null;
  canAct: boolean;
  expanded: boolean;
  presetFlag: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}) {
  const review = useReviewBillingValidation(period);
  const detail = useBillingValidationDetail(period, item?.item_key ?? null);
  const [status, setStatus] = useState<ReviewStatus>("pending");
  const [notes, setNotes] = useState("");
  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (item && item.item_key !== seeded.current) {
      seeded.current = item.item_key;
      setStatus(presetFlag ? "flagged" : item.review_status);
      setNotes(item.review_notes ?? "");
      if (presetFlag) setTimeout(() => notesRef.current?.focus(), 150);
    }
    if (!item) seeded.current = null;
  }, [item, presetFlag]);

  const dirty =
    !!item &&
    (status !== item.review_status || (notes ?? "") !== (item.review_notes ?? ""));

  function save() {
    if (!item) return;
    review.mutate(
      { itemKey: item.item_key, status, notes },
      {
        onSuccess: () => toast.success("Review saved"),
        onError: (e) =>
          toast.error("Couldn't save review", { description: (e as Error).message }),
      },
    );
  }

  const record = detail.data?.record ?? null;

  return (
    <EntityDrawer
      open={item != null}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      title={item?.entity_name ?? item?.entity_id ?? ""}
      subtitle={check ? check.title : undefined}
      onClose={onClose}
    >
      {item && (
        <div className="flex flex-col gap-5 p-4">
          {/* What's wrong, and what to do about it */}
          {check && (
            <div
              className={cn(
                "rounded-md border px-4 py-3 text-sm",
                check.severity === "danger"
                  ? "border-danger/40 bg-danger-light/40"
                  : "border-warning/40 bg-warning-light/40",
              )}
            >
              <p className="font-semibold">{check.title}</p>
              <p className="mt-1 text-muted-foreground">{check.why}</p>
              <p className="mt-1.5">
                <b>Fix:</b> {check.fix}
              </p>
            </div>
          )}

          {item.is_billing && (
            <div className="rounded-md border border-danger/40 bg-danger-light/50 px-4 py-3 text-sm text-danger">
              <b>Billing {fmtCur2(item.billed)}</b> over the last three months
              with no contract behind it.
            </div>
          )}

          {/* Verdict */}
          <div className="space-y-3 rounded-md border px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Review
              </span>
              <StatusBadge value={item.review_status} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Verdict
                active={status === "approved"}
                activeClass="border-success bg-success-light text-success"
                onClick={() => setStatus("approved")}
                disabled={!canAct}
              >
                <Check className="h-3.5 w-3.5" /> Accept
              </Verdict>
              <Verdict
                active={status === "flagged"}
                activeClass="border-warning bg-warning-light text-warning"
                onClick={() => setStatus("flagged")}
                disabled={!canAct}
              >
                <Flag className="h-3.5 w-3.5" /> Flag
              </Verdict>
              <Verdict
                active={status === "pending"}
                activeClass="border-border bg-muted text-foreground"
                onClick={() => setStatus("pending")}
                disabled={!canAct}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Clear
              </Verdict>
            </div>
            <Textarea
              ref={notesRef}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canAct}
              rows={3}
              aria-label="Review notes"
              placeholder={
                status === "flagged"
                  ? "What has to change before this can close?"
                  : "Optional notes — e.g. why this one is fine as it stands…"
              }
            />
            {item.reviewed_at && (
              <p className="text-xs text-muted-foreground">
                Last reviewed by {item.reviewed_by_name ?? "—"} on{" "}
                {new Date(item.reviewed_at).toLocaleString()}.
              </p>
            )}
            <div className="flex items-center gap-3">
              <Button onClick={save} disabled={!canAct || !dirty || review.isPending}>
                {review.isPending ? "Saving…" : "Save review"}
              </Button>
              {!canAct && (
                <span className="text-xs text-muted-foreground">
                  Only a RevOps owner can record a verdict.
                </span>
              )}
            </div>
          </div>

          {/* The record itself */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {item.entity} record
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {item.entity_id}
              </span>
            </div>

            {detail.isLoading && (
              <div className="h-24 animate-pulse rounded-md border bg-muted/30" />
            )}
            {detail.data?.error && (
              <p className="rounded-md border border-dashed px-3 py-4 text-xs text-muted-foreground">
                {detail.data.error}
              </p>
            )}
            {record && item.entity === "account" && (
              <AccountBody record={record} expanded={expanded} />
            )}
            {record && item.entity === "contract" && (
              <ContractBody record={record} expanded={expanded} />
            )}
            {record && item.entity === "company" && (
              <CompanyBody record={record} expanded={expanded} />
            )}
          </div>
        </div>
      )}
    </EntityDrawer>
  );
}

/** An account's evidence: its QuickBooks identity, the companies hanging off
 *  it, and the contracts that either exist or conspicuously don't. */
function AccountBody({ record, expanded }: { record: Rec; expanded: boolean }) {
  const companies = arr(record.companies);
  const contracts = arr(record.contracts);
  const qbId = record.quickbooks_id;
  return (
    <div className="space-y-4">
      <Grid>
        <Field label="Account name">{str(record.name)}</Field>
        <Field label="QuickBooks ID">
          {qbId == null || qbId === "" ? (
            <span className="font-medium text-danger">not set</span>
          ) : (
            <ExtLink href={qboCustomerUrl(String(qbId))}>{String(qbId)}</ExtLink>
          )}
        </Field>
        <Field label="QuickBooks customer">{str(record.quickbooks_customer_name)}</Field>
        <Field label="Companies">{companies.length}</Field>
        <Field label="Contracts">
          <span className={contracts.length === 0 ? "font-medium text-danger" : undefined}>
            {contracts.length}
          </span>
        </Field>
        <Field label="Created">{fmtDate(record.created_when as string)}</Field>
      </Grid>

      {/* The whole point of the no-contract check: real companies underneath,
          nothing contractual on top. */}
      <Section title={`Companies on this account (${companies.length})`}>
        {companies.length === 0 ? (
          <Empty>No companies — the account may simply be unused.</Empty>
        ) : (
          <ul className="space-y-1 text-sm">
            {(expanded ? companies : companies.slice(0, 6)).map((c, i) => (
              <li key={String(c.id ?? i)} className="flex justify-between gap-3">
                <span className="truncate">{str(c.name)}</span>
              </li>
            ))}
            {!expanded && companies.length > 6 && (
              <li className="text-xs text-muted-foreground">
                +{companies.length - 6} more — expand to see them all
              </li>
            )}
          </ul>
        )}
      </Section>

      <Section title={`Contracts (${contracts.length})`}>
        {contracts.length === 0 ? (
          <Empty>
            No contracts on this account. That is the finding — anything it bills
            is unsupported.
          </Empty>
        ) : (
          <ul className="space-y-1 text-sm">
            {contracts.map((k, i) => (
              <li key={String(k.id ?? i)} className="flex justify-between gap-3">
                <span className="truncate">{str(k.company_name)}</span>
                <span className="flex-shrink-0 text-muted-foreground">
                  {k.mrr_total != null ? `${fmtCur2(num(k.mrr_total))}/mo · ` : ""}
                  {fmtDate(k.sign_date as string)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/** A contract's evidence: the links that are missing, and what it's worth. */
function ContractBody({ record, expanded }: { record: Rec; expanded: boolean }) {
  const lineItems = arr(record.contract_line_items);
  return (
    <div className="space-y-4">
      <Grid>
        <Field label="Company">{str(record.company_name)}</Field>
        <Field label="DBA">{str(record.company_dba)}</Field>
        <Field label="Type">{str(record.contract_type_name)}</Field>
        <Field label="Status">{str(record.contract_status)}</Field>
        <Field label="MRR">{fmtCur2(num(record.mrr_total))}</Field>
        <Field label="Sign date">{fmtDate(record.sign_date as string)}</Field>
        <Field label="Billing from">{fmtDate(record.billing_effective_from as string)}</Field>
        <Field label="Closer">{str(record.closer_name)}</Field>
      </Grid>

      {/* The links this check is about, each called out when absent rather
          than rendered as an empty dash you have to interpret. */}
      <Section title="Links">
        <Grid>
          <Field label="Account">
            {record.account ? (
              str(record.account_name)
            ) : (
              <span className="font-medium text-danger">not assigned</span>
            )}
          </Field>
          <Field label="Assigned company">
            {record.assigned_company_id ? (
              str(record.assigned_company_name)
            ) : (
              <span className="font-medium text-danger">not assigned</span>
            )}
          </Field>
          <Field label="Reseller contract">
            {record.reseller_contract_id ? (
              str(
                (record.reseller_contract_details as Rec | null)?.company_name ??
                  record.reseller_contract_id,
              )
            ) : (
              <span className="font-medium text-danger">no master contract</span>
            )}
          </Field>
          {record.reseller_contract_details != null && (
            <Field label="Master type">
              {str((record.reseller_contract_details as Rec).contract_type_name)}
            </Field>
          )}
        </Grid>
      </Section>

      {expanded && (
        <Section title={`Line items (${lineItems.length})`}>
          {lineItems.length === 0 ? (
            <Empty>No line items on this contract.</Empty>
          ) : (
            <ul className="space-y-1 text-sm">
              {lineItems.map((li, i) => (
                <li key={String(li.uuid ?? i)} className="flex justify-between gap-3">
                  <span className="truncate">
                    {str(
                      (li.item_detail as Rec | null)?.name ??
                        (li.package_detail as Rec | null)?.name,
                    )}
                  </span>
                  <span className="flex-shrink-0 tabular-nums text-muted-foreground">
                    {fmtCur2(num(li.item_total))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}
    </div>
  );
}

/** A company's evidence: which account it claims, and whether it's live. */
function CompanyBody({ record, expanded }: { record: Rec; expanded: boolean }) {
  return (
    <div className="space-y-4">
      <Grid>
        <Field label="Company">{str(record.name)}</Field>
        <Field label="Account">
          {record.account ? (
            str(record.account_name ?? record.account)
          ) : (
            <span className="font-medium text-danger">not assigned</span>
          )}
        </Field>
        <Field label="Active">{yesNo(record.is_active)}</Field>
        <Field label="Status">{str(record.status_name)}</Field>
        <Field label="Created">{fmtDate(record.created_when as string)}</Field>
        <Field label="Updated">{fmtDate(record.updated_when as string)}</Field>
      </Grid>

      {expanded && (
        <Section title="Where it is">
          <Grid>
            <Field label="Address">{str(record.address1)}</Field>
            <Field label="City">{str(record.city)}</Field>
            <Field label="State">{str(record.state)}</Field>
            <Field label="ZIP">{str(record.zip_code)}</Field>
            <Field label="Phone">{str(record.phone)}</Field>
          </Grid>
        </Section>
      )}
    </div>
  );
}

// ── small presentational helpers ────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </span>
      <div className="rounded-md border px-4 py-3">{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 rounded-md border px-4 py-3 sm:grid-cols-3">
      {children}
    </dl>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-mono text-xs text-brand underline-offset-2 hover:underline"
    >
      {children}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function Verdict({
  active,
  activeClass,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  activeClass: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50",
        active ? activeClass : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
